// libraries.
import { Form, Row, Col } from "react-bootstrap";
import { UseFormRegister, UseFormWatch, UseFormSetValue } from "react-hook-form";

// interfaces.
import { RawTool } from "@store/apiSlices/incentiveRules/interfaces/rawRules.interfaces";

// types.
import { FormValues } from "./useCreateRuleForm";

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  idx: number;
  register: UseFormRegister<FormValues>;
  watch: UseFormWatch<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  tools: RawTool[];
  canRemove: boolean;
  onRemove: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const ActionEditor = ({ idx, register, watch, setValue, tools, canRemove, onRemove }: Props) => {
  const selectedTool = watch(`actions.${idx}.tool`);
  const selectedOperation = watch(`actions.${idx}.operation`);

  const toolData = tools.find((t) => t.name === selectedTool);
  const operationData = toolData?.operations.find((op) => op.operation === selectedOperation);
  const paramKeys = Object.keys(operationData?.params ?? {});

  return (
    <div className="p-2 rounded" style={{ border: "1px solid #e0e0e0", background: "#fafafa" }}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span style={{ fontSize: 12, fontWeight: 600, color: "#666" }}>Acción {idx + 1}</span>
        {canRemove && (
          <button type="button" className="btn-remove-item" onClick={onRemove}>
            ✕ Eliminar
          </button>
        )}
      </div>

      <Row className="g-2 mb-2">
        <Col xs={6}>
          <Form.Label style={{ fontSize: 12 }}>Nombre de la acción</Form.Label>
          <Form.Control size="sm" placeholder="ej: aplicar_bono_2pct" {...register(`actions.${idx}.actionName`, { required: "Requerido" })} />
        </Col>
        <Col xs={6}>
          <Form.Label style={{ fontSize: 12 }}>Variable de salida (output)</Form.Label>
          <Form.Control size="sm" placeholder="ej: bono_aplicado" {...register(`actions.${idx}.output`)} />
        </Col>
      </Row>

      <Row className="g-2 mb-2">
        <Col xs={6}>
          <Form.Label style={{ fontSize: 12 }}>Tool</Form.Label>
          <Form.Select
            size="sm"
            {...register(`actions.${idx}.tool`, { required: "Requerido" })}
            onChange={(e) => {
              setValue(`actions.${idx}.tool`, e.target.value);
              setValue(`actions.${idx}.operation`, "");
              setValue(`actions.${idx}.params`, {});
            }}
          >
            <option value="">Seleccionar tool...</option>
            {tools.map((t) => (
              <option key={t.name} value={t.name}>{t.name} — {t.description}</option>
            ))}
          </Form.Select>
        </Col>
        <Col xs={6}>
          <Form.Label style={{ fontSize: 12 }}>Operación</Form.Label>
          <Form.Select
            size="sm"
            {...register(`actions.${idx}.operation`, { required: "Requerido" })}
            onChange={(e) => {
              setValue(`actions.${idx}.operation`, e.target.value);
              setValue(`actions.${idx}.params`, {});
            }}
            disabled={!selectedTool}
          >
            <option value="">Seleccionar operación...</option>
            {toolData?.operations.map((op) => (
              <option key={op.operation} value={op.operation}>{op.operation} — {op.description}</option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {paramKeys.length > 0 && (
        <div className="mt-1">
          <p style={{ fontSize: 11, color: "#888", marginBottom: 6 }}>Parámetros de la operación:</p>
          <Row className="g-2">
            {paramKeys.map((paramKey) => {
              const paramMeta = operationData!.params[paramKey];
              return (
                <Col xs={6} key={paramKey}>
                  <Form.Label style={{ fontSize: 11 }}>
                    {paramKey}
                    {paramMeta.required && <span className="text-danger"> *</span>}
                    <span className="text-muted"> ({paramMeta.type})</span>
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    placeholder={`ej: ${paramMeta.type === "float" ? "0.02" : "${variable}"}`}
                    title={paramMeta.description}
                    {...register(`actions.${idx}.params.${paramKey}`, {
                      required: paramMeta.required ? "Requerido" : false,
                    })}
                  />
                </Col>
              );
            })}
          </Row>
        </div>
      )}
    </div>
  );
};
